import React, {useEffect, useState} from 'react';
import {Outlet, useLocation} from 'react-router';
import * as Sentry from "@sentry/react";
import { Trans } from '@lingui/react/macro';

/*
  Layout route for a group of pages: a page that crashes is replaced by an error message while the header stays usable.
  Each group's backLink, declared next to its routes in Routes.js, says where to go instead.
  Crashes outside the pages still reach the root boundary in index.js.
*/
function PageErrorBoundary({backLink}) {
    // every group renders this same component in the same place, so React keeps one boundary across all of
    // them and its error survives navigation; a new key is how it forgets the crash once the guest moves on
    const [boundaryKey, setBoundaryKey] = useState(0);

    return (
        <Sentry.ErrorBoundary
            key={boundaryKey}
            fallback={<PageErrorFallback backLink={backLink} onLeave={() => setBoundaryKey(key => key + 1)} />}
        >
            <Outlet />
        </Sentry.ErrorBoundary>
    );
}

function PageErrorFallback({backLink, onLeave}) {
    const {pathname} = useLocation();
    const [crashedPathname] = useState(pathname);

    useEffect(() => {
        if (pathname !== crashedPathname) {
            onLeave();
        }
    }, [pathname, crashedPathname, onLeave]);

    return (
        <div className="container mx-auto mt-6">
            <p>
                <Trans id="unexpected_error_try_again">An unexpected error has happened please try again.</Trans>
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-6">
                <button className="action-button action-button-enabled px-6" onClick={() => window.location.reload()}>
                    <Trans id="try_again">Try again</Trans>
                </button>
                {backLink}
            </div>
        </div>
    );
}

export default PageErrorBoundary;
